import type { CapabilitiesResponse } from '@/domains/shared/api/queries/useCapabilitiesQuery';

type CapabilitiesData = CapabilitiesResponse['capabilities'];

export type CapabilityRouteRule = {
  id: string;
  matches: (path: string) => boolean;
  isAllowed: (capabilities: CapabilitiesData) => boolean;
  fallback: (capabilities: CapabilitiesData) => string;
};

export const DEFAULT_CAPABILITY_FALLBACK = '/hive/beehive/summary';

export const capabilityRouteRules: CapabilityRouteRule[] = [
  // Add a rule here when a capability should protect a whole route or route subtree.
  {
    id: 'withdrawals',
    matches: (path) =>
      path === '/hive/beehive/actions/withdraw' ||
      path.startsWith('/hive/beehive/actions/withdraw/'),
    isAllowed: (capabilities) => capabilities.withdraw.allowed,
    fallback: () => DEFAULT_CAPABILITY_FALLBACK,
  },
];

export const resolveRuleFallback = (
  rule: CapabilityRouteRule,
  capabilities: CapabilitiesData,
) => rule.fallback(capabilities);

type CapabilityRouteResolution = {
  path: string;
  gated: boolean;
  allowed: boolean;
  ruleId: string | null;
};

export const resolveCapabilityRoute = (
  targetPath: string,
  capabilities: CapabilitiesData | null | undefined,
): CapabilityRouteResolution => {
  // The gate uses this result to either render the page or redirect to the rule's fallback.
  const rule = capabilityRouteRules.find(({ matches }) => matches(targetPath));

  if (!rule) {
    return { path: targetPath, gated: false, allowed: true, ruleId: null };
  }

  if (!capabilities || rule.isAllowed(capabilities)) {
    return {
      path: targetPath,
      gated: true,
      allowed: true,
      ruleId: rule.id,
    };
  }

  return {
    path: rule.fallback(capabilities),
    gated: true,
    allowed: false,
    ruleId: rule.id,
  };
};
